import React, { memo, useMemo, useState } from "react"
import styled from "styled-components"
import clsx from "clsx"
import BodyText, { BodyTextProps } from "components/Typography/BodyText"
import { MdInfo } from "react-icons/md"
import { Colors } from "themes/palette"
import IconOpenEye from "../icons/IconOpenEye"
import IconCloseEye from "../icons/IconCloseEye"
import { validatePassword } from "utils/auth/authHelper"
import Caption from "components/Typography/Caption"
import IconOpenEyeError from "../icons/IconOpenEyeError"
import IconCloseEyeError from "../icons/IconCloseEyeError"
import ErrorIconSmall from "../icons/ErrorIconSmall"
import InputMask, { BeforeMaskedStateChangeStates, InputState } from "react-input-mask"
import CharacterCount from "components/CharacterCount"

const InputSection = styled.div`
  position: relative;

  &.search-input {
    width: 100%;
  }
`

const StyledInput = styled(InputMask)`
  display: block;
  width: 340px;
  height: 48px;

  padding: 24px 16px;

  font-size: 14px;
  line-height: 17px;

  :focus,
  &.shrink {
    outline: none;
  }

  background-color: transparent;
  color: ${(props) => props.theme.palette.primary_one};

  border: 1px solid ${(props) => props.theme.palette.primary_one};
  border-radius: 8px;

  &.error {
    border: 1px solid ${(props) => props.theme.palette.error};
    color: ${(props) => props.theme.palette.error};
  }

  &.fullWidth {
    width: 100%;
  }

  &.disabled {
    color: ${(props) => props.theme.palette.disabled};
    border-color: ${(props) => props.theme.palette.disabled};

    &::placeholder {
      color: ${(props) => props.theme.palette.disabled};
    }
  }

  &.readOnly {
    color: ${(props) => props.theme.palette.subtitle};
    border-color: ${(props) => props.theme.palette.subtitle};
  }

  ::placeholder {
    font-size: 14px;
    line-height: 18px;
    font-style: normal;
    font-weight: 400;
    color: ${(props) => props.theme.palette.primary_one};
  }

  &.isPassword {
    padding-right: 60px;
  }
`

const ErrorText = styled.span`
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: ${(props) => props.theme.palette.error};
  display: flex;
  align-items: center;
  margin-top: 8px;
  gap: 4px;
  min-height: 17px;

  &.unvisible {
    width: 0px;
    overflow: hidden;
  }
`

const ErrorContainerPassword = styled.div`
  display: flex;
  gap: 4px;
  color: ${(props) => props.theme.palette.error};
  margin-top: 8px;
  min-height: 17px;

  &.unvisible {
    width: 0px;
    overflow: hidden;
  }
`

const BottomContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const InformativeMessage = styled.span`
  display: flex;
  align-items: center;
  margin-top: 4px;
  margin-bottom: 12px;
  gap: 4px;

  &.hidden {
    width: 0px;
    overflow: hidden;
  }
`

const InputWrapper = styled.div`
  position: relative;
`

const LabelForm = styled.label`
  position: absolute;
  pointer-events: none;
  left: 15px;
  top: 14px;
  transition: 0.2s ease all;
  color: ${(props) => props.theme.palette.primary_one};
  ${StyledInput}:focus ~ &,
  ${StyledInput}:not(:placeholder-shown) ~ &,
  &.shrink,
  ${StyledInput}:-webkit-autofill ~ & {
    top: -8px;
    padding: 0 4px 0 4px;
    font-size: 12px;
    background-color: ${(props) => props.theme.palette.white};
  }

  &.disabled {
    color: ${(props) => props.theme.palette.disabled};
  }

  &.error {
    color: ${(props) => props.theme.palette.error};
  }
`

const Label = styled.div`
  font-size: 12px;
  line-height: 16px;

  &.disabled {
    color: ${(props) => props.theme.palette.disabled};
    line-height: 19px;
  }

  &.error {
    color: ${(props) => props.theme.palette.error};
  }

  &.primary {
    color: ${(props) => props.theme.palette.primary_one};
  }

  &.readOnly {
    color: ${(props) => props.theme.palette.subtitle};
  }
`

const OpenPassword = styled.div`
  position: absolute;
  right: 32px;
  top: 14px;
  transition: 0.2s ease all;
  color: ${(props) => props.theme.palette.primary_one};
  z-index: 1000;

  &.error {
    color: ${(props) => props.theme.palette.error};
  }
`

const ContainerPasswordError = styled.div`
  display: flex;
  flex-direction: column;
`

const ContainerListErrors = styled.ul`
  display: flex;
  flex-direction: column;
  list-style-type: disc;
  margin-left: 24px;
`

interface InputProps {
  id?: string
  type?: React.HTMLInputTypeAttribute
  name: string
  label: string
  className?: string
  placeholder?: string
  error?: string | boolean
  value?: string | number | any
  mask?: string | string[] | RegExp[]
  maskPlaceholder?: string | null | undefined
  maskChar?: string | null
  safeBottom?: boolean
  safeBottomInformative?: boolean
  beforeMaskedStateChange?: (states: BeforeMaskedStateChangeStates) => InputState
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  shrink?: boolean
  disabled?: boolean
  required?: boolean
  informativeMessage?: string
  color?: BodyTextProps["color"]
  colorIcon?: string
  informativeMessagePosition?: "top" | "bottom"
  autoFocus?: boolean
  readOnly?: boolean
  maxLength?: number
  sameValue?: boolean
  limitCharacters?: number
  disabledCharacter?: boolean
}

const Input: React.FC<InputProps> = (props: InputProps) => {
  const {
    type,
    className,
    error,
    id,
    label,
    beforeMaskedStateChange,
    mask = "",
    maskPlaceholder,
    maskChar = "_",
    name,
    onChange,
    onFocus,
    placeholder,
    value,
    safeBottom = false,
    safeBottomInformative = false,
    shrink = false,
    disabled = false,
    required = true,
    informativeMessage,
    color,
    colorIcon = Colors.disabled,
    informativeMessagePosition = "bottom",
    autoFocus = false,
    readOnly = false,
    maxLength,
    sameValue = false,
    limitCharacters,
    disabledCharacter = false
  } = props

  const errorVisible = error ? "" : "unvisible"
  const showErrorSection = safeBottom || error

  const isPassword = type === "password"

  const [showPassword, setShowPassword] = useState(false)

  const togglePassword = () => {
    setShowPassword(!showPassword)
  }

  const activeType = useMemo(() => {
    if (!isPassword) return type

    return showPassword ? "text" : "password"
  }, [isPassword, type, showPassword])

  const informativeMessageVisible = informativeMessage ? "" : "hidden"
  const showInformativeMessageSection = safeBottomInformative || informativeMessage

  const labelColor = useMemo(() => {
    if (disabled) return "disabled"

    if (readOnly) return "readOnly"

    return error ? "error" : "primary_one"
  }, [disabled, error])

  const passwordValidation = useMemo(() => {
    if (!value || !isPassword) return null

    const errors: string[] = []

    if (value.length < 8) {
      errors.push("8 caracteres")
    }

    if (value.length > 80) {
      errors.push("Até 80 caracteres")
    }

    if (!value.match(/\d/)) {
      errors.push("1 número")
    }

    if (!value.match(/[!@#$%^&*]/)) {
      errors.push("1 caracter especial (permitidos: !@#$%^&*)")
    }

    if (!value.match(/[A-Z]/)) {
      errors.push("1 letra maiúscula")
    }

    if (!value.match(/[a-z]/)) {
      errors.push("1 letra minúscula")
    }

    if (!validatePassword(value) && errors.length === 0) {
      errors.push("Certifique-se de que a senha siga as opções anteriores para que seja uma senha forte.")
    }

    return { hasError: errors.length > 0, errors }
  }, [value, isPassword])

  return (
    <InputSection className={className}>
      {showInformativeMessageSection && informativeMessagePosition === "top" && (
        <InformativeMessage id="input-error" className={clsx(className, informativeMessageVisible)}>
          <MdInfo size={16} color={colorIcon} />
          <BodyText size="small" color={color}>
            {informativeMessage}
          </BodyText>
        </InformativeMessage>
      )}
      <InputWrapper>
        <StyledInput
          required={required}
          id={id}
          type={activeType}
          name={name}
          onChange={onChange}
          onFocus={onFocus}
          className={clsx(
            `${className} ${clsx({ error: !!error })}`,
            { shrink },
            { disabled },
            { readOnly },
            { isPassword }
          )}
          value={value}
          placeholder={placeholder || " "}
          mask={mask}
          maskPlaceholder={maskPlaceholder}
          maskChar={maskChar}
          beforeMaskedStateChange={beforeMaskedStateChange}
          disabled={disabled || readOnly}
          autoFocus={autoFocus}
          maxLength={maxLength}
        />
        <LabelForm htmlFor={id} className={clsx(className, { shrink })}>
          <Label className={labelColor}>{label}</Label>
        </LabelForm>
        {showErrorSection && !isPassword && (
          <BottomContainer>
            <ErrorText id="input-error" className={clsx(className, errorVisible)}>
              <ErrorIconSmall />
              <Caption size="large" color="error">
                {error}
              </Caption>
            </ErrorText>
            {limitCharacters && (
              <CharacterCount
                currentLength={value?.length || 0}
                maxLength={limitCharacters}
                disabledCharacter={disabledCharacter}
              />
            )}
          </BottomContainer>
        )}
        {showErrorSection && isPassword && (
          <ErrorContainerPassword id="input-error-password" className={clsx(className, errorVisible)}>
            <ErrorIconSmall />
            {sameValue && (
              <Caption size="large" color="error">
                {error}
              </Caption>
            )}
            {!sameValue && passwordValidation?.hasError && (
              <ContainerPasswordError>
                <Caption size="large" color="error">
                  Senha inválida. A senha deve conter pelo menos:
                </Caption>
                <ContainerListErrors>
                  {passwordValidation.errors.map((error) => (
                    <li key={error}>
                      <Caption size="large" color="error">
                        {error}
                      </Caption>
                    </li>
                  ))}
                </ContainerListErrors>
              </ContainerPasswordError>
            )}
          </ErrorContainerPassword>
        )}
        {isPassword && (
          <OpenPassword onClick={togglePassword}>
            {showPassword && <IconOpenEye />}
            {!showPassword && <IconCloseEye />}
          </OpenPassword>
        )}
        {!!error && isPassword && (
          <OpenPassword onClick={togglePassword}>
            {showPassword && <IconOpenEyeError />}
            {!showPassword && <IconCloseEyeError />}
          </OpenPassword>
        )}
      </InputWrapper>
      {showInformativeMessageSection && informativeMessagePosition === "bottom" && (
        <InformativeMessage id="input-error" className={clsx(className, informativeMessageVisible)}>
          <MdInfo size={16} color={colorIcon} />
          <BodyText size="small" color={color}>
            {informativeMessage}
          </BodyText>
        </InformativeMessage>
      )}
    </InputSection>
  )
}

export default memo(Input)
